import { useCallback } from 'react';
import { IBudgetsListResponse, fetchBudgetById } from '../../../../api/fetchBudget';
import { BudgetStatusEnum } from '../../../../enums/budgetStatus';
import { BudgetRecipientEnum, IBudgetFormData } from '../../createAndEdit/types';
import { useListJourneySelector } from './useListJourneySelector';

interface IUseListNavigationParams {
  occurrenceId: string;
  budgetsListResponse: IBudgetsListResponse | null | undefined;
  userProfile: BudgetRecipientEnum | undefined;
  onNavigateToCreate: () => void;
  onNavigateToView: (budgetId: string, data: IBudgetFormData) => void;
  onNavigateToEdit: (budgetId: string, data: IBudgetFormData) => void;
}

export const useListNavigation = (params: IUseListNavigationParams) => {
  const {
    occurrenceId,
    budgetsListResponse,
    userProfile,
    onNavigateToCreate,
    onNavigateToView,
    onNavigateToEdit,
  } = params;

  const loadBudgetData = useCallback(
    async (budgetId: string, versionNumber?: number): Promise<IBudgetFormData | null> => {
      try {
        const budgetItem = budgetsListResponse?.budgets.find((item) => item.uuid === budgetId);
        const version = versionNumber || budgetItem?.lastVersionNumber || 1;

        const budgetData = await fetchBudgetById(budgetId, occurrenceId, version);
        if (budgetData) {
          if (budgetItem) {
            budgetData.status = budgetItem.status as BudgetStatusEnum;
          }
        return budgetData;
      }
      return null;
    } catch (error) {
      return null;
    }
    },
    [occurrenceId, budgetsListResponse],
  );

  const { handleViewOrEditBudget } = useListJourneySelector({
    budgetsListResponse,
    userProfile,
    onNavigateToView,
    onNavigateToEdit,
    loadBudgetData,
  });

  const handleCreateBudget = useCallback(() => {
    onNavigateToCreate();
  }, [onNavigateToCreate]);

  const handleViewBudget = useCallback(
    async (budgetId: string, versionNumber?: number) => {
      const budgetData = await loadBudgetData(budgetId, versionNumber);
      if (budgetData) {
        onNavigateToView(budgetId, budgetData);
      }
    },
    [loadBudgetData, onNavigateToView],
  );

  const handleEditBudget = useCallback(
    async (budgetId: string) => {
      const budgetItem = budgetsListResponse?.budgets.find((item) => item.uuid === budgetId);
      const versionNumber = budgetItem?.lastVersionNumber || 1;
      const budgetData = await loadBudgetData(budgetId, versionNumber);
      if (budgetData) {
        budgetData.versionNumber = versionNumber;
        if (budgetItem) {
          budgetData.status = budgetItem.status as BudgetStatusEnum;
          if (budgetItem.recipient) {
            budgetData.recipient = budgetItem.recipient;
          }
        }
        onNavigateToEdit(budgetId, budgetData);
      }
    },
    [loadBudgetData, onNavigateToEdit, budgetsListResponse],
  );

  return {
    handleCreateBudget,
    handleViewBudget,
    handleEditBudget,
    handleViewOrEditBudget,
  };
};

